import AccessRulesForm from 'components/Forms/AccessRules';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { PlusIcon, SaveIcon, EditIcon, InfoIcon } from 'lucide-react';
import React from 'react';
import { getAccessRules, putAccessRules } from 'utils/fetchers';

const AccessRulesList = ({ data }: IAccessRulesList) => {
  const [accessRules, setAccessRules] = React.useState<IAccessRule[]>([]);
  const [editingAR, setEditingAR] = React.useState<Partial<IAccessRule & { new?: true }> | null>(null);

  React.useEffect(() => {
    getAccessRules(data.organization, data.userId).then(ars => {
      if (Array.isArray(ars)) setAccessRules(ars);
    });
  }, []);

  const onSaveClickAR = () => {
    if (editingAR?.application && editingAR.rule) {
      if (editingAR.new) {
        putAccessRules(editingAR.application, editingAR.rule, data.organization, data.userId).then(suc => {
          if (suc) {
            setAccessRules([{ application: editingAR.application!, rule: editingAR.rule! }, ...accessRules]);
            setEditingAR(null);
          }
        });
      } else {
        putAccessRules(editingAR.application, editingAR.rule, data.organization, data.userId).then(suc => {
          if (suc) {
            setAccessRules(
              accessRules.map(ars =>
                ars.application === editingAR.application
                  ? { application: editingAR.application!, rule: editingAR.rule! }
                  : ars,
              ),
            );
            setEditingAR(null);
          }
        });
      }
    }
  };

  const onEditARField = (field: keyof IAccessRule, data: string) => {
    if (editingAR) setEditingAR({ ...editingAR, [field]: data });
  };

  return (
    <div>
      <div>
        <div className="flex w-full mb-2">
          <div className="w-50">
            <Label>Access rules</Label>
          </div>
          <div className="flex w-full justify-end">
            <Button variant="outline" className="h-9" onClick={() => setEditingAR({ new: true })}>
              <PlusIcon strokeWidth={2} />
              <div>New Access Rule</div>
            </Button>
          </div>
        </div>
        <div className="tableArea rounded-md">
          <table>
            <thead>
              <tr>
                <th>App</th>
                <th>
                  Rule
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoIcon size={15} className="ml-2" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <div>
                        <span className="font-bold mr-2 mb-4">always-allow</span>Every user is allowed access,
                        regardless of more specific access rule(s)
                      </div>
                      <div>
                        <span className="font-bold mr-2 mb-4">always-deny</span>Every user is denied access, regardless
                        of more specific access rule(s)
                      </div>
                      <div>
                        <span className="font-bold mr-2 mb-4">default-deny</span>User is only allowed if they are
                        affected by more specific access rule(s) that allow access, and if not defaults to denying
                        access
                      </div>
                      <div>
                        <span className="font-bold mr-2 mb-4">default-allow</span>User is only denied if they are
                        affected by more specific access rule(s) that deny access, and if not defaults to allowing
                        access
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </th>
                <th style={{ width: 'auto' }}></th>
              </tr>
            </thead>
            <tbody>
              {[...(editingAR?.new ? [editingAR] : []), ...accessRules].map(ar => (
                <tr key={'new' in ar && ar?.new ? 'new' : ar.application}>
                  <AccessRulesForm
                    ar={editingAR && editingAR?.application === ar.application ? editingAR : ar}
                    editing={editingAR?.application === ar.application}
                    onEditField={onEditARField}
                    apps={accessRules.map(ars => ars.application)}
                  />
                  <td>
                    {editingAR && editingAR.application === ar.application ? (
                      <>
                        <Button
                          onClick={onSaveClickAR}
                          disabled={!editingAR.application || !editingAR.rule}
                          className="h-9"
                        >
                          <SaveIcon /> Save
                        </Button>
                      </>
                    ) : editingAR ? (
                      <></>
                    ) : (
                      <div className="flex w-full justify-end">
                        <Button size={'icon'} className="ml-2" onClick={() => setEditingAR(ar)}>
                          <EditIcon />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

interface IAccessRulesList {
  data: { organization?: string; userId?: string };
}

export default AccessRulesList;
